import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inputClass, labelClass, inlineErrorClass, inlineSuccessClass } from '@/components/ui/inputStyles';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';

type ConfigResp = {
  ok: boolean;
  default_media_quota_mb?: number;
  default_media_quota_bytes?: number;
  max_ai_profiles_per_user?: number;
};

export function SystemConfigPage() {
  const { capabilities, loading: sessLoading } = useSession();
  const [loading, setLoading] = useState(true);
  const [quotaMb, setQuotaMb] = useState('512');
  const [maxAiProfiles, setMaxAiProfiles] = useState('10');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<ConfigResp>('get_wes_system_config', { method: 'GET' });
      if (r.ok) {
        if (r.default_media_quota_mb != null) {
          setQuotaMb(String(r.default_media_quota_mb));
        }
        if (r.max_ai_profiles_per_user != null) {
          setMaxAiProfiles(String(r.max_ai_profiles_per_user));
        }
      }
    } catch {
      setErr('加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessLoading || !capabilities.manage_users) return;
    void load();
  }, [load, sessLoading, capabilities.manage_users]);

  async function save() {
    const mb = parseInt(quotaMb.trim(), 10);
    const maxAi = parseInt(maxAiProfiles.trim(), 10);
    if (Number.isNaN(mb) || mb < 1) {
      setErr('限额须为 1～102400 MB 的整数');
      return;
    }
    if (Number.isNaN(maxAi) || maxAi < 1 || maxAi > 50) {
      setErr('每用户 AI 配置数须为 1～50 的整数');
      return;
    }
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<ConfigResp>('save_wes_system_config', {
        method: 'POST',
        body: JSON.stringify({ default_media_quota_mb: mb, max_ai_profiles_per_user: maxAi }),
      });
      if (r.ok) {
        setMsg('已保存');
        if (r.default_media_quota_mb != null) {
          setQuotaMb(String(r.default_media_quota_mb));
        }
        if (r.max_ai_profiles_per_user != null) {
          setMaxAiProfiles(String(r.max_ai_profiles_per_user));
        }
      } else {
        setErr('保存失败');
      }
    } catch {
      setErr('保存失败');
    } finally {
      setSaving(false);
    }
  }

  if (!sessLoading && !capabilities.manage_users) {
    return <Navigate to="/dashboard" replace />;
  }

  if (sessLoading || loading) {
    return <Spinner label="加载系统参数" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="全局系统参数"
        description="仅系统管理员可见。此处设置全平台默认的多媒体本地存储限额；新建用户默认继承该值，可在用户管理中单独调整。"
      />
      <PcRecommendedCallout>系统级参数建议在电脑端配置并保存。</PcRecommendedCallout>

      <Card title="多媒体存储">
        <p className="mb-4 text-sm text-slate-600">
          用户未单独设置时，采用此限额统计其名下站点、小程序等本地图片占用。达到限额后将禁止上传，需配置
          <strong> OSS</strong> 并迁移资源。
        </p>
        <label className={labelClass}>
          <span>默认本地多媒体限额（MB）</span>
          <input
            type="number"
            min={1}
            max={102400}
            className={inputClass}
            value={quotaMb}
            onChange={(e) => setQuotaMb(e.target.value)}
          />
        </label>
        <p className="mt-2 text-xs text-slate-500">推荐 SaaS 场景：512～2048 MB；大客户可单独在用户管理中提高。</p>
        <div className="mt-4 flex gap-2">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void save()}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
        {msg ? <p className={inlineSuccessClass + ' mt-3'}>{msg}</p> : null}
        {err ? <p className={inlineErrorClass + ' mt-3'}>{err}</p> : null}
      </Card>

      <Card title="AI 接口引擎">
        <p className="mb-4 text-sm text-slate-600">
          每位普通用户可在「AI 接口引擎」中维护多套大模型配置（API Key 仅存服务端）。此处限制单账号最多可创建的数量。
        </p>
        <label className={labelClass}>
          <span>每用户最多 AI 配置数</span>
          <input
            type="number"
            min={1}
            max={50}
            className={inputClass}
            value={maxAiProfiles}
            onChange={(e) => setMaxAiProfiles(e.target.value)}
          />
        </label>
        <p className="mt-2 text-xs text-slate-500">默认 10；范围 1～50。</p>
        <p className="mt-3 text-xs text-slate-500">与上方「多媒体存储」共用同一保存按钮。</p>
      </Card>
    </div>
  );
}
